import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'

import {
  backToStart,
  getUser,
  getOrganization,
  setStep,
  submitUserEmail,
} from './state'

import {
  LookingToJoin,
  HorizontalRule,
  Form,
  Main,
  Button,
} from '../../../components'
import { JoinDefaultProps } from './types'
import { EmailInput } from '../../../components/Forms/Email'
import { useCreateEmailVerificationCodeMutation } from '../../../remote/graphql'
import { JoinSteps } from './JoinSteps'
import { FormattedMessage, useIntl } from 'react-intl'

type EmailFormData = {
  email: string
  recaptchaToken: string
}

export function EmailForm({
  organizationInviteToken,
  superEnabled,
}: JoinDefaultProps) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const organization = useSelector(getOrganization)
  const handleBackToStart = () => dispatch(backToStart())
  const [createEmailVerificationCode, { loading }] =
    useCreateEmailVerificationCodeMutation()

  const { formState, handleSubmit, control, setError } = useForm<EmailFormData>(
    {
      mode: 'onChange',
      defaultValues: { recaptchaToken: ' ', email: user?.email ?? '' },
    }
  )

  const isWithinBoundaries = (value: string) =>
    organizationInviteToken ||
    (organization.authDomains || '')
      .split(',')
      .some((domain: string) => domain === value.split('@')[1])
      ? true
      : intl.formatMessage(
          {
            id: 'boarding.organization.join.email.invalid',
            defaultMessage: `This email cannot be used to join {organizationName}.
          Please use an email that is in the authorized domain list:
          {authDomains}`,
          },
          {
            organizationName: organization.name,
            authDomains: organization.authDomains,
          }
        )

  const onSubmit = async ({ email }: EmailFormData) => {
    if (!email) {
      return
    }

    dispatch(submitUserEmail({ email }))

    try {
      const { data } = await createEmailVerificationCode({
        variables: { input: { email, fromSuper: superEnabled } },
      })
      if (data?.createEmailVerificationCode.success) {
        return dispatch(setStep(JoinSteps.EmailVerification))
      }
    } catch {
      setError('email', {
        type: 'server',
        message:
          'There was an error while creating your email verification code.',
      })
    }
  }

  return (
    <Main.Container footer={<LookingToJoin superEnabled={superEnabled} />}>
      <Main.Title>
        <FormattedMessage
          id="boarding.organization.join.title"
          defaultMessage="Join the {product} team"
          values={{ product: superEnabled ? 'Super' : 'Slite' }}
        />
        <br />
        <span className="capitalize">{organization.domain}</span>
      </Main.Title>
      <form
        className="flexbox flexbox-column form-grid"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Form.Label>
          <FormattedMessage
            id="boarding.organization.join.email"
            defaultMessage="Type in your work email"
            values={{}}
          />
        </Form.Label>
        <EmailInput<EmailFormData>
          control={control}
          name="email"
          rules={{
            validate: {
              isWithinBoundaries,
            },
          }}
          placeholder="name@company.com"
          loading={loading}
          autoFocus
        />

        <Form.Button
          className="width-full"
          disabled={!formState.isValid || loading}
        >
          {intl.formatMessage({
            id: 'boarding.organization.join.continue',
            defaultMessage: 'Continue with email',
          })}
        </Form.Button>
      </form>
      <HorizontalRule />
      <Button variant="secondary" onClick={handleBackToStart} type="button">
        <FormattedMessage
          id="boarding.organization.join.withSlack"
          defaultMessage="Use your Google or Slack account"
          values={{}}
        />
      </Button>
    </Main.Container>
  )
}
